import { REPUB_COUNTRY_BY_REPUB, type RequestContext } from '@cx-web/domain-types';

import { type BettingInsightsUsageContext } from '~/configuration/types';
import { getCoupons, getProducts, getWallType } from '~/configuration/utils/configHelpers';
import { componentTags } from '~/constants/component-tags';
import { getSearchConfig } from '~/search-domain';
import {
  bettingSplitsIndex,
  eventOverviewIndex,
  matchupCentreIndex,
  playerDataIndex,
  positiveEvIndex,
  premiumMarketMoversIndex,
  type SearchDomainIndexName,
  trendsIndex,
} from '~/search-domain/indexes';

const tagsMap: Partial<Record<SearchDomainIndexName, readonly string[]>> = {
  [positiveEvIndex]: [componentTags.POSITIVE_VALUE_BETS],
  [trendsIndex]: [componentTags.TRENDS],
  [bettingSplitsIndex]: [componentTags.PUBLIC_BETTING_SPLITS],
  [eventOverviewIndex]: [componentTags.MATCH_CENTRE],
  [matchupCentreIndex]: [componentTags.MATCH_CENTRE],
  [playerDataIndex]: [componentTags.MATCH_CENTRE],
  [premiumMarketMoversIndex]: [componentTags.PREMIUM_MARKET_MOVERS],
};

const getTagsForIndex = (indexName: SearchDomainIndexName) => tagsMap[indexName] ?? [];

export function createBettingInsightsBaseConfig(
  indexName: SearchDomainIndexName,
  requestContext: RequestContext,
  options: { usageContext?: BettingInsightsUsageContext } = {},
) {
  const { usageContext = 'default' } = options;
  const tags = [...getTagsForIndex(indexName)];

  const products = getProducts(requestContext.user);
  const wallType = getWallType(requestContext.user, tags);
  const coupons = getCoupons(products);

  const repubCountry = REPUB_COUNTRY_BY_REPUB[requestContext.repubConfig.name];

  const searchConfig = getSearchConfig(indexName, {
    repubCountry,
    repubConfig: requestContext.repubConfig,
    usageContext,
  });

  if (!searchConfig) {
    throw new Error(`Missing search config for index: ${indexName}`);
  }

  const isWalled = wallType !== undefined;
  const searchAttributes = searchConfig.searchAttributes;
  const sortingFn = searchConfig.sorting;

  return {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,

    tags,
    usageContext,
    wallType,
    isWalled,
    products,
    coupons,

    searchAttributes,
    sortingFn,
  };
}
